import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/ui/Toast';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const DEFAULT = {
  questionText: '',
  questionType: 'scale',
  isRequired: true,
  helpText: '',
  scaleMin: '1',
  scaleMax: '5',
  scaleMinLabel: '',
  scaleMaxLabel: ''
};

const QuestionBuilder = ({ onAddQuestion, editingQuestion, onUpdateQuestion, onCancelEdit }) => {
  // Replaces toastError(): non-blocking and styled; errors persist until
  // dismissed rather than vanishing on a timer.
  const { error: toastError } = useToast();
  const { t } = useTranslation('reviews');
  const [form, setForm] = useState(DEFAULT);

  useEffect(() => {
    if (editingQuestion) {
      setForm({
        questionText: editingQuestion.questionText || '',
        questionType: editingQuestion.questionType || 'scale',
        isRequired: editingQuestion.isRequired !== false,
        helpText: editingQuestion.helpText || '',
        scaleMin: String(editingQuestion.scaleMin ?? 1),
        scaleMax: String(editingQuestion.scaleMax ?? 5),
        scaleMinLabel: editingQuestion.scaleMinLabel || '',
        scaleMaxLabel: editingQuestion.scaleMaxLabel || ''
      });
    } else {
      setForm(DEFAULT);
    }
  }, [editingQuestion?.id]);

  const questionTypeOptions = [
    { value: 'scale', label: t('schemaBuilder.questionBuilder.types.scale') },
    { value: 'true_false', label: t('schemaBuilder.questionBuilder.types.true_false') },
    { value: 'short_text', label: t('schemaBuilder.questionBuilder.types.short_text') },
    { value: 'long_text', label: t('schemaBuilder.questionBuilder.types.long_text') }
  ];

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e?.target?.value ?? e }));

  const buildQuestion = () => {
    const q = {
      questionText: form.questionText?.trim(),
      questionType: form.questionType,
      isRequired: form.isRequired,
      helpText: form.helpText?.trim() || null
    };
    if (form.questionType === 'scale') {
      q.scaleMin = parseInt(form.scaleMin) || 1;
      q.scaleMax = parseInt(form.scaleMax) || 5;
      q.scaleMinLabel = form.scaleMinLabel?.trim() || null;
      q.scaleMaxLabel = form.scaleMaxLabel?.trim() || null;
    }
    return q;
  };

  const handleSubmit = () => {
    if (!form.questionText?.trim()) {
      toastError(t('schemaBuilder.questionBuilder.enterQuestionAlert'));
      return;
    }
    if (editingQuestion) {
      onUpdateQuestion(editingQuestion.id, buildQuestion());
    } else {
      onAddQuestion(buildQuestion());
      setForm(DEFAULT);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <h2 className="text-lg font-semibold text-foreground mb-6">
        {editingQuestion ? t('schemaBuilder.questionBuilder.editTitle') : t('schemaBuilder.questionBuilder.addTitle')}
      </h2>

      <div className="space-y-5">
        <Input
          label={t('schemaBuilder.questionBuilder.questionText')}
          type="text"
          placeholder={t('schemaBuilder.questionBuilder.questionTextPlaceholder')}
          value={form.questionText}
          onChange={set('questionText')}
          required
        />

        <Select
          label={t('schemaBuilder.questionBuilder.questionType')}
          options={questionTypeOptions}
          value={form.questionType}
          onChange={(value) => setForm(prev => ({ ...prev, questionType: value }))}
          required
        />

        {form.questionType === 'scale' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label={t('schemaBuilder.questionBuilder.minValue')}
                type="number"
                placeholder="1"
                value={form.scaleMin}
                onChange={set('scaleMin')}
                min="1"
                max="10"
              />
              <Input
                label={t('schemaBuilder.questionBuilder.maxValue')}
                type="number"
                placeholder="5"
                value={form.scaleMax}
                onChange={set('scaleMax')}
                min="1"
                max="10"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label={t('schemaBuilder.questionBuilder.minLabel')}
                type="text"
                placeholder={t('schemaBuilder.questionBuilder.minLabelPlaceholder')}
                value={form.scaleMinLabel}
                onChange={set('scaleMinLabel')}
              />
              <Input
                label={t('schemaBuilder.questionBuilder.maxLabel')}
                type="text"
                placeholder={t('schemaBuilder.questionBuilder.maxLabelPlaceholder')}
                value={form.scaleMaxLabel}
                onChange={set('scaleMaxLabel')}
              />
            </div>
          </div>
        )}

        <Input
          label={t('schemaBuilder.questionBuilder.helpText')}
          type="text"
          placeholder={t('schemaBuilder.questionBuilder.helpTextPlaceholder')}
          value={form.helpText}
          onChange={set('helpText')}
          description={t('schemaBuilder.questionBuilder.helpTextDesc')}
        />

        <Checkbox
          label={t('schemaBuilder.questionBuilder.required')}
          checked={form.isRequired}
          onChange={(e) => setForm(prev => ({ ...prev, isRequired: e?.target?.checked }))}
          description={t('schemaBuilder.questionBuilder.requiredDesc')}
        />

        <div className="flex justify-end gap-3 pt-2 border-t border-border">
          {editingQuestion ? (
            <>
              <Button variant="outline" onClick={onCancelEdit}>
                {t('schemaBuilder.questionBuilder.cancel')}
              </Button>
              <Button onClick={handleSubmit} iconName="Check" iconPosition="left">
                {t('schemaBuilder.questionBuilder.update')}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setForm(DEFAULT)}>
                {t('schemaBuilder.questionBuilder.clear')}
              </Button>
              <Button onClick={handleSubmit} iconName="Plus" iconPosition="left">
                {t('schemaBuilder.questionBuilder.add')}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionBuilder;
